import React, { useEffect, useRef, useState } from "react";

/**
 * /sunrise-pitch — Private pitch landing page for Sunrise Radio UK.
 * Standalone visual identity (music-industry palette, NOT The Reset Sanctuary).
 * Designed to be sent as a direct link to the decision-maker.
 * No nav, no footer cross-links — single-purpose page that closes one deal.
 */

const GOLD = "#d4a72c";
const INK = "#0b0b0d";
const CREAM = "#f3ecdf";
const ACCENT = "#ff3b30"; // bold accent for Mocktail UK call-outs

const Stat = ({ value, label, testId }) => (
    <div data-testid={testId} style={{ textAlign: "left" }}>
        <div className="font-serif" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 400, color: GOLD, lineHeight: 0.95, letterSpacing: "-0.02em" }}>{value}</div>
        <div style={{ fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(243,236,223,0.55)", marginTop: 8, fontWeight: 500 }}>{label}</div>
    </div>
);

const SectionLabel = ({ children, testId }) => (
    <p data-testid={testId} style={{ color: GOLD, fontSize: 11, letterSpacing: "0.4em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 18px" }}>
        {children}
    </p>
);

const SunrisePitch = () => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);

    useEffect(() => {
        document.title = "TaTvA K × Sunrise Radio — Daily Show Pitch";
        // Try to autoplay showreel (muted) — most browsers allow it
        const v = videoRef.current;
        if (v) {
            v.muted = true;
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        }
    }, []);

    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !v.muted;
        v.muted = next;
        setMuted(next);
        if (!next) {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        }
    };

    return (
        <main
            data-testid="sunrise-pitch-page"
            style={{
                background: `radial-gradient(circle at 20% 0%, #1a1a1f 0%, ${INK} 55%)`,
                color: CREAM,
                fontFamily: "Inter, sans-serif",
                minHeight: "100vh",
                paddingBottom: 100,
            }}
        >
            {/* TOP STRIP */}
            <div style={{ borderBottom: `1px solid rgba(212,167,44,0.18)`, padding: "18px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <p style={{ margin: 0, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", color: GOLD, fontWeight: 700 }}>
                    Private Pitch · TaTvA K → Sunrise Radio UK
                </p>
                <p style={{ margin: 0, fontSize: 10.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(243,236,223,0.45)", fontWeight: 500 }}>
                    Weekend Slot · Prepared June 2026 · Dubai → London
                </p>
            </div>

            {/* HERO */}
            <section data-testid="hero" style={{ maxWidth: 1180, margin: "0 auto", padding: "60px 28px 50px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.45fr 1fr", gap: 56, alignItems: "center" }} className="rs-hero-grid">
                    <div>
                        <p style={{ color: ACCENT, fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 22px" }}>
                            A Weekend Pitch to Sunrise Radio
                        </p>
                        <h1 className="font-serif" style={{ fontSize: "clamp(40px, 6.6vw, 80px)", fontWeight: 400, lineHeight: 0.98, letterSpacing: "-0.025em", margin: "0 0 26px", color: CREAM }}>
                            I had the honour of holding India's nightly prime-time slot for <span style={{ color: GOLD, fontStyle: "italic" }}>13 years.</span><br/>
                            I'd love the chance to bring that craft to <span style={{ color: GOLD, fontStyle: "italic" }}>Sunrise weekends.</span>
                        </h1>
                        <p style={{ fontSize: 16, lineHeight: 1.65, color: "rgba(243,236,223,0.78)", maxWidth: 620, margin: "0 0 30px", fontWeight: 300 }}>
                            <strong style={{ color: CREAM, fontWeight: 500 }}>TaTvA K.</strong> 13 years on Zabardast Hit 95 FM &mdash; one of India's longest-running nightly prime-time slots, 9 PM to 3 AM, six nights a week, sponsored continuously throughout its run. Two iTunes Top 100 collaborations with UK Asian artists, kind words from BBC and Bobby Friction, four years on the Sunburn main stage, millions of YouTube views across my own channels and across podcast / collab features, and a label catalogue that has crossed 8 million streams. I'm Dubai-based, multilingual, and would be grateful for the opportunity to contribute to Sunrise Radio's weekend audience.
                        </p>
                        <p style={{ color: GOLD, fontSize: 11.5, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: 0 }}>
                            ↓ Showreel &amp; audio samples below ↓
                        </p>
                    </div>
                    <div data-testid="hero-portrait-wrap" style={{ position: "relative" }}>
                        <div style={{
                            position: "absolute", inset: "-14px -14px -14px 14px",
                            border: `1px solid rgba(212,167,44,0.4)`,
                            pointerEvents: "none",
                        }} />
                        <img
                            src="/brochure-assets/tatvak-portrait-web.jpg"
                            alt="TaTvA K — Portrait"
                            data-testid="hero-portrait"
                            style={{
                                width: "100%",
                                display: "block",
                                position: "relative",
                                filter: "contrast(1.05) saturate(1.05)",
                                boxShadow: "0 40px 100px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(212,167,44,0.08)",
                            }}
                        />
                        <p style={{ position: "absolute", bottom: -28, right: 0, margin: 0, color: GOLD, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700 }}>
                            TaTvA K · Dubai
                        </p>
                    </div>
                </div>
            </section>

            {/* SHOWREEL VIDEO */}
            <section data-testid="showreel" style={{ maxWidth: 1100, margin: "0 auto", padding: "0 28px 70px" }}>
                <div
                    onClick={toggleMute}
                    data-testid="showreel-wrap"
                    style={{
                        position: "relative",
                        width: "100%",
                        background: "#000",
                        border: `1px solid rgba(212,167,44,0.3)`,
                        boxShadow: "0 40px 100px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(212,167,44,0.05)",
                        cursor: "pointer",
                        overflow: "hidden",
                    }}
                >
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        style={{ width: "100%", display: "block" }}
                        data-testid="showreel-video"
                    >
                        <source src="/brochure-assets/tatvak-showreel-web.mp4" type="video/mp4" />
                    </video>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                        aria-label={muted ? "Unmute" : "Mute"}
                        data-testid="showreel-mute-toggle"
                        style={{
                            position: "absolute",
                            bottom: 16,
                            right: 16,
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            background: "rgba(0,0,0,0.6)",
                            border: `1px solid ${GOLD}`,
                            color: GOLD,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            backdropFilter: "blur(8px)",
                        }}
                    >
                        {muted ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                        )}
                    </button>
                </div>
                <p style={{ marginTop: 14, textAlign: "center", color: "rgba(243,236,223,0.45)", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase" }}>
                    Tap to unmute · Showreel · Dubai → India
                </p>
            </section>

            {/* THE SHOW CONCEPT */}
            <section data-testid="show-concept" style={{ maxWidth: 1100, margin: "0 auto", padding: "30px 28px 80px" }}>
                <SectionLabel testId="label-show">The Show — Concept</SectionLabel>
                <h2 className="font-serif" style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 400, lineHeight: 1.05, letterSpacing: "-0.02em", margin: "0 0 28px", color: CREAM }}>
                    Weekend prime evening.<br/>
                    7-minute packages. <span style={{ color: GOLD, fontStyle: "italic" }}>No dead air.</span>
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "flex-start" }} className="rs-grid-collapse">
                    <div>
                        <p style={{ fontSize: 15.5, lineHeight: 1.75, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: "0 0 18px" }}>
                            A Friday-Saturday-Sunday weekend slot, built around the format I refined over 13 years at Zabardast: <strong style={{ color: CREAM }}>7-minute fully packaged chunks</strong>, Hindi-Punjabi-Haryanvi-English code-switch when it serves the moment, RJ over the mix, no autopilot playlists.
                        </p>
                        <p style={{ fontSize: 15.5, lineHeight: 1.75, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: 0 }}>
                            Tuned to the British South Asian weekend rhythm &mdash; Friday wind-down after work, Saturday house-party energy, Sunday family-dinner-into-late-call-home-to-India. The show would be co-named with Sunrise; final identity is yours to shape.
                        </p>
                    </div>
                    <div style={{ borderLeft: `1px solid rgba(212,167,44,0.2)`, paddingLeft: 28 }}>
                        <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 16px" }}>Per Episode Format</p>
                        <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14.5, lineHeight: 1.9, color: "rgba(243,236,223,0.85)", fontWeight: 300 }}>
                            <li>· <strong style={{ color: CREAM }}>Mixes music across all eras</strong> &mdash; 90s gold to today's chart</li>
                            <li>· <strong style={{ color: CREAM }}>Cold open mix</strong> &mdash; 90 sec, no talk</li>
                            <li>· <strong style={{ color: CREAM }}>Themed 7-min package</strong> &mdash; signature segment</li>
                            <li>· <strong style={{ color: CREAM }}>Diaspora dedications</strong> &mdash; live calls / WhatsApp messages</li>
                            <li>· <strong style={{ color: CREAM }}>Throwback hour</strong> &mdash; rotating era per weekend</li>
                            <li>· <strong style={{ color: CREAM }}>Imaka exclusives</strong> &mdash; label pre-air premieres</li>
                            <li>· <strong style={{ color: CREAM }}>Late-weekend mix</strong> &mdash; closing 30 min, club-grade</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* THE NUMBERS */}
            <section data-testid="numbers" style={{ background: "rgba(212,167,44,0.04)", borderTop: `1px solid rgba(212,167,44,0.15)`, borderBottom: `1px solid rgba(212,167,44,0.15)` }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "70px 28px" }}>
                    <SectionLabel testId="label-numbers">Some Numbers · For Context</SectionLabel>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 36, marginTop: 20 }}>
                        <Stat testId="stat-years" value="13 yrs" label="Daily prime-time radio · Zabardast Hit 95 FM" />
                        <Stat testId="stat-rooh" value="200M+" label="Views · ROOH (household Punjabi hit)" />
                        <Stat testId="stat-streams" value="8.4M+" label="Streams · Imaka Music · 6 months" />
                        <Stat testId="stat-monthly" value="217.8K" label="Spotify monthly listeners" />
                        <Stat testId="stat-reels" value="1M+" label="Views per reel · consistent" />
                        <Stat testId="stat-sunburn" value="4 yrs" label="Sunburn main stage · back-to-back with Top 100 DJs" />
                        <Stat testId="stat-langs" value="6" label="Languages · Pj · Hi · En · Hr · Ml · Bn" />
                        <Stat testId="stat-platinum" value="Platinum" label="Latest global single" />
                        <Stat testId="stat-youtube" value="Millions" label="YouTube views · own channels &amp; podcast / collab features" />
                    </div>
                </div>
            </section>

            {/* UK TRACK RECORD */}
            <section data-testid="uk-track" style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 28px 30px" }}>
                <SectionLabel testId="label-uk">Why UK Listeners May Already Know Me</SectionLabel>
                <h2 className="font-serif" style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, lineHeight: 1.1, letterSpacing: "-0.015em", margin: "0 0 36px", color: CREAM, maxWidth: 820 }}>
                    A few touchpoints that have brought my work to <span style={{ color: GOLD, fontStyle: "italic" }}>British ears.</span>
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 28 }}>
                    {[
                        { title: "Bohemia · Surjit Khan · Gitaj Bindrakhia", body: "Direct work alongside some of the most respected names in Punjabi music &mdash; spanning the heritage line that defined UK Punjabi listening." },
                        { title: "RDB camp · indirect", body: "Connection into the founding RDB family through one of the brothers &mdash; the sound that gave UK Asian music its global edge through the 2000s and 2010s." },
                        { title: "Juggy D × TaTvA K", body: "\"We Are Punjabi\" &mdash; iTunes Top 100 chart for 11+ months. Co-produced with one of the UK's most-played Asian artists." },
                        { title: "Bee2 × TaTvA K", body: "Production collaboration with Birmingham's veteran UK Asian producer. The track lives in UK club rotation." },
                        { title: "BBC UK · Feature", body: "Featured artist segment. BBC's editorial team has already greenlit my sound for British ears." },
                        { title: "Bobby Friction · BBC Asian Network", body: "Sunset Track of the Day on BBC Asian Network &mdash; Friction's tastemaker stamp is the UK Asian gold standard." },
                        { title: "Sabras Radio · Interview", body: "Full-length artist interview with Leicester's leading South Asian radio brand." },
                        { title: "Pathaan's Auto Rickshaw Show", body: "Featured artist on one of the most respected UK Asian music shows." },
                    ].map((item, i) => (
                        <div key={i} data-testid={`uk-card-${i}`} style={{ border: `1px solid rgba(212,167,44,0.22)`, padding: "22px 22px 24px", background: "rgba(255,255,255,0.015)" }}>
                            <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 12px" }}>{item.title}</p>
                            <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: 0 }}>{item.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* COLLABORATIONS & PRODUCTION */}
            <section data-testid="collab" style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 28px" }}>
                <SectionLabel testId="label-collab">The Production Side</SectionLabel>
                <h2 className="font-serif" style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, lineHeight: 1.1, letterSpacing: "-0.015em", margin: "0 0 28px", color: CREAM, maxWidth: 820 }}>
                    Hosting is half the offer. I bring the <span style={{ color: GOLD, fontStyle: "italic" }}>other half</span> &mdash; exclusive music the station can premiere.
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36, alignItems: "flex-start" }} className="rs-grid-collapse">
                    <div>
                        <p style={{ fontSize: 15, lineHeight: 1.75, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: "0 0 16px" }}>
                            I run <strong style={{ color: CREAM }}>Imaka Music</strong> &mdash; an independent label that crossed 8.4M+ streams in its first six months. Sunrise would have first-listen rights on every release: pre-air exclusives, station-only IDs, branded jingles, holiday specials.
                        </p>
                        <p style={{ fontSize: 15, lineHeight: 1.75, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: 0 }}>
                            I produced <em>"32 Bore"</em> with Fazilpuria (Haryanvi #1), official refixes of Sidhu Moosewala for <strong style={{ color: CREAM }}>T-Series Main Channel</strong> (views in the tens of millions), and the <em>SHVAIK</em> and <em>DHAYE DHAYE</em> Haryanvi tracks. <strong style={{ color: CREAM }}>Currently producing for Simran Kaur Dhadli</strong> &mdash; one of the leading voices in the current Punjabi female-artist wave. There's a steady pipeline of original music I'd be glad to share.
                        </p>
                    </div>
                    <div style={{ borderLeft: `1px solid rgba(212,167,44,0.2)`, paddingLeft: 28 }}>
                        <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 16px" }}>Featured / Covered In</p>
                        <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14, lineHeight: 2, color: "rgba(243,236,223,0.85)", fontWeight: 300 }}>
                            <li>· Rolling Stone</li>
                            <li>· Vogue</li>
                            <li>· Verve Magazine</li>
                            <li>· Times of India</li>
                            <li>· T-Series Main Channel</li>
                            <li>· Times Music</li>
                            <li>· Top 10 DJs of India listing</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* AUDIO SNIPPETS — 5 broadcast samples from the Zabardast run */}
            <section data-testid="audio-snippets" style={{ maxWidth: 1100, margin: "0 auto", padding: "30px 28px 70px" }}>
                <SectionLabel testId="label-audio">Broadcast Samples · Archival</SectionLabel>
                <h2 className="font-serif" style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, lineHeight: 1.1, letterSpacing: "-0.015em", margin: "0 0 18px", color: CREAM, maxWidth: 820 }}>
                    Five clips from my Zabardast run &mdash; <span style={{ color: GOLD, fontStyle: "italic" }}>the format in action.</span>
                </h2>
                <p style={{ fontSize: 14.5, lineHeight: 1.7, color: "rgba(243,236,223,0.72)", maxWidth: 820, margin: "0 0 30px", fontWeight: 300 }}>
                    These are archival recordings from <em>Mocktail Reloaded</em>, my long-running show at Zabardast Hit 95 FM. Each clip is a complete 7-minute themed package &mdash; cold open, code-switch RJ, mix, segue. The show name and IP belong to Zabardast; the craft and approach travel with me. Sunrise's weekend show would be its own brand, jointly named with your team.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
                    {[
                        { label: "Set 07 · Ep 116", file: "tatvak-snippet-1.mp3" },
                        { label: "Set 02 · Ep 117", file: "tatvak-snippet-2.mp3" },
                        { label: "Set 05 · Ep 119", file: "tatvak-snippet-3.mp3" },
                        { label: "Set 06 · Ep 119", file: "tatvak-snippet-4.mp3" },
                        { label: "Set 07 · Ep 119", file: "tatvak-snippet-5.mp3" },
                    ].map((snip, i) => (
                        <div key={i} data-testid={`snippet-${i + 1}`} style={{ border: `1px solid rgba(212,167,44,0.22)`, padding: "18px 18px 16px", background: "rgba(255,255,255,0.015)" }}>
                            <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 12px" }}>{snip.label}</p>
                            <audio
                                controls
                                preload="none"
                                style={{ width: "100%", filter: "invert(0.92) hue-rotate(180deg)" }}
                                data-testid={`snippet-audio-${i + 1}`}
                            >
                                <source src={`/brochure-assets/${snip.file}`} type="audio/mpeg" />
                            </audio>
                        </div>
                    ))}
                </div>
            </section>

            {/* THE 30-NIGHT WEEKEND RITUAL */}
            <section data-testid="ritual" style={{ background: "rgba(212,167,44,0.05)", borderTop: `1px solid rgba(212,167,44,0.18)`, borderBottom: `1px solid rgba(212,167,44,0.18)` }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "70px 28px" }}>
                    <SectionLabel testId="label-ritual">A 30-Night Weekend Ritual</SectionLabel>
                    <h2 className="font-serif" style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, lineHeight: 1.1, letterSpacing: "-0.015em", margin: "0 0 24px", color: CREAM, maxWidth: 820 }}>
                        Ten weekends. Thirty nights of broadcast.
                    </h2>
                    <p style={{ fontSize: 15.5, lineHeight: 1.75, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: "0 0 16px", maxWidth: 820 }}>
                        I'd be grateful for a 30-night weekend window &mdash; Friday, Saturday, Sunday for ten consecutive weekends &mdash; to deliver the format on Sunrise's air. Fully packaged 7-minute chunks daily, broadcast-ready, station-branded, sponsor-safe.
                    </p>
                    <p style={{ fontSize: 15.5, lineHeight: 1.75, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: 0, maxWidth: 820 }}>
                        I show up, I deliver the sets, I bring my catalogue, my network, and the craft I've sharpened over 13 years on air. The rest is your call.
                    </p>
                </div>
            </section>

            {/* THE ASK */}
            <section data-testid="the-ask" style={{ maxWidth: 1100, margin: "0 auto", padding: "90px 28px 60px" }}>
                <SectionLabel testId="label-ask">The Ask</SectionLabel>
                <h2 className="font-serif" style={{ fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 400, lineHeight: 1.0, letterSpacing: "-0.02em", margin: "0 0 32px", color: CREAM }}>
                    One <span style={{ color: GOLD, fontStyle: "italic" }}>weekend slot.</span><br/>
                    I deliver the sets.<br/>
                    You pay for the work.
                </h2>
                <p style={{ fontSize: 16.5, lineHeight: 1.7, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: "0 0 22px", maxWidth: 720 }}>
                    Fully packaged 7-minute chunks delivered across ten weekends &mdash; broadcast-ready, station-branded, sponsor-safe. My label premieres go pre-air on your station. My UK collaborator network is available for guest sessions, drops and station IDs.
                </p>
                <p style={{ fontSize: 16.5, lineHeight: 1.7, color: "rgba(243,236,223,0.82)", fontWeight: 300, margin: "0 0 36px", maxWidth: 720 }}>
                    I bring the craft, the catalogue, and 13 years of nightly broadcast discipline. Sunrise brings the audience, the slot and the brand. The rest is a conversation between professionals.
                </p>
                <p style={{ color: GOLD, fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, margin: 0 }}>
                    A 30-minute call would be a wonderful start &mdash; whenever it suits your team.
                </p>
            </section>

            {/* CONTACT */}
            <section data-testid="contact" style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 28px 60px", borderTop: `1px solid rgba(212,167,44,0.18)` }}>
                <div style={{ paddingTop: 36, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 28 }}>
                    <div>
                        <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>Direct</p>
                        <p style={{ margin: 0, fontSize: 14.5, color: CREAM, lineHeight: 1.7 }}>
                            TaTvA K (Tatva)<br/>
                            <a href="mailto:iamtatvak@gmail.com" data-testid="contact-email" style={{ color: CREAM, textDecoration: "underline", textUnderlineOffset: 3 }}>iamtatvak@gmail.com</a><br/>
                            Dubai · GMT+4
                        </p>
                    </div>
                    <div>
                        <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>Listen / Watch</p>
                        <p style={{ margin: 0, fontSize: 14.5, color: CREAM, lineHeight: 1.7 }}>
                            <a href="https://instagram.com/tatvakofficial" target="_blank" rel="noopener noreferrer" data-testid="contact-ig" style={{ color: CREAM, textDecoration: "underline", textUnderlineOffset: 3 }}>@tatvakofficial</a><br/>
                            Spotify · YouTube (Imaka Music)<br/>
                            217.8K monthly listeners
                        </p>
                    </div>
                    <div>
                        <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>Availability</p>
                        <p style={{ margin: 0, fontSize: 14.5, color: CREAM, lineHeight: 1.7 }}>
                            Friday-Saturday-Sunday<br/>
                            UK prime evening slot<br/>
                            <strong style={{ color: GOLD, fontWeight: 600 }}>Ready to go on air tomorrow</strong>
                        </p>
                    </div>
                </div>

                <p style={{ marginTop: 50, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(243,236,223,0.35)", textAlign: "center" }}>
                    Confidential · For Sunrise Radio UK · Not for distribution
                </p>
            </section>

            <style>{`
                @media (max-width: 900px) {
                    .rs-hero-grid { grid-template-columns: 1fr !important; gap: 36px !important; }
                    .rs-hero-grid > div:last-child { max-width: 380px; margin: 0 auto; }
                }
                @media (max-width: 760px) {
                    .rs-grid-collapse { grid-template-columns: 1fr !important; gap: 24px !important; }
                    .rs-grid-collapse > div + div { border-left: none !important; padding-left: 0 !important; border-top: 1px solid rgba(212,167,44,0.2); padding-top: 24px !important; }
                }
            `}</style>
        </main>
    );
};

export default SunrisePitch;
